import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  Checkbox,
  Alert,
  Spin,
  message,
  Typography,
  Space,
  Tag,
} from 'antd';
import { syncFeishuRequirements } from '@/api/requirements';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  onSuccess?: () => void;
}

interface FieldChange {
  field_name: string;
  field_key: string;
  db_value: string;
  feishu_value: string;
}

interface ChangeDetail {
  req_id: string;
  operation_type: string;
  operation_description: string;
  field_changes: FieldChange[];
  new_requirement_info?: {
    description: string;
    planStage: string;
    catagory: string;
    catagory2: string;
    subcategory: string;
    priority: string;
    reqSource?: string;
  };
  existing_requirement_info?: {
    description: string;
    planStage: string;
    catagory: string;
    catagory2: string;
    subcategory: string;
    priority: string;
    reqSource?: string;
  };
}

interface OperationStats {
  total_changes: number;
  add_count: number;
  update_count: number;
  delete_count: number;
  reverse_sync_delete_count?: number;
}

const OP_COLOR: Record<string, string> = {
  '新增': 'green',
  '修改': 'blue',
  '删除': 'red',
  '反向同步删除': 'orange',
};

/** 展开行：显示字段变更明细、新增/删除需求信息 */
const ExpandedRow: React.FC<{ detail: ChangeDetail }> = ({ detail }) => {
  const fieldColumns = [
    { title: '字段', dataIndex: 'field_name', width: 120 },
    {
      title: '数据库原值',
      dataIndex: 'db_value',
      render: (v: string) => (
        <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 280, display: 'block' }}>
          {v || '(空)'}
        </Text>
      ),
    },
    {
      title: '飞书新值',
      dataIndex: 'feishu_value',
      render: (v: string) => (
        <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 280, display: 'block' }}>
          {v || '(空)'}
        </Text>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 16px', background: '#fafafa' }}>
      {/* 字段变更明细 */}
      {detail.field_changes && detail.field_changes.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>字段变更：</Text>
          <Table
            dataSource={detail.field_changes}
            columns={fieldColumns}
            rowKey="field_key"
            size="small"
            pagination={false}
            bordered
          />
        </div>
      )}

      {/* 新增需求信息 */}
      {detail.new_requirement_info && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>新增需求信息：</Text>
          <div style={{ fontSize: 12, color: '#595959', lineHeight: '20px' }}>
            <div><Text strong>描述：</Text>{detail.new_requirement_info.description}</div>
            <div><Text strong>交付阶段：</Text>{detail.new_requirement_info.planStage}</div>
            <div><Text strong>需求分类：</Text>{detail.new_requirement_info.catagory}</div>
            <div><Text strong>二级分类：</Text>{detail.new_requirement_info.catagory2}</div>
            <div><Text strong>需求分组：</Text>{detail.new_requirement_info.subcategory}</div>
            <div><Text strong>优先级：</Text>{detail.new_requirement_info.priority}</div>
            {detail.new_requirement_info.reqSource && (
              <div><Text strong>备注：</Text>{detail.new_requirement_info.reqSource}</div>
            )}
          </div>
        </div>
      )}

      {/* 现有需求信息（删除时展示） */}
      {detail.existing_requirement_info && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>现有需求信息：</Text>
          <div style={{ fontSize: 12, color: '#595959', lineHeight: '20px' }}>
            <div><Text strong>描述：</Text>{detail.existing_requirement_info.description}</div>
            <div><Text strong>交付阶段：</Text>{detail.existing_requirement_info.planStage}</div>
            <div><Text strong>需求分类：</Text>{detail.existing_requirement_info.catagory}</div>
            <div><Text strong>二级分类：</Text>{detail.existing_requirement_info.catagory2}</div>
            <div><Text strong>需求分组：</Text>{detail.existing_requirement_info.subcategory}</div>
            <div><Text strong>优先级：</Text>{detail.existing_requirement_info.priority}</div>
            {detail.existing_requirement_info.reqSource && (
              <div><Text strong>备注：</Text>{detail.existing_requirement_info.reqSource}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FeishuSyncModal: React.FC<Props> = ({
  open,
  onClose,
  projectId,
  projectName,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [feishuLink, setFeishuLink] = useState('');
  const [changeDetails, setChangeDetails] = useState<ChangeDetail[]>([]);
  const [operationStats, setOperationStats] = useState<OperationStats | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [phaseMsg, setPhaseMsg] = useState('');

  useEffect(() => {
    if (open) {
      setStep('form');
      setFeishuLink('');
      setChangeDetails([]);
      setOperationStats(null);
      setSelectedIds(new Set());
      setSelectAll(true);
      setExpandedRowKeys([]);
      setLoading(false);
    }
  }, [open]);

  const buildPayload = (extra: Record<string, unknown>) => ({
    feishu_link: feishuLink,
    id: projectId,
    project: projectName,
    user_confirmed: false,
    user_cancelled: false,
    ...extra,
  });

  const performSync = async (extra: Record<string, unknown> = {}) => {
    const res = await syncFeishuRequirements(projectId, buildPayload(extra));
    return res.data;
  };

  const handleSync = async () => {
    if (!feishuLink.trim()) {
      message.error('飞书链接不能为空');
      return;
    }
    setPhaseMsg('需求回流中...');
    setLoading(true);
    try {
      const data = await performSync();
      handleResponse(data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || '请求失败，请重试');
    } finally {
      setPhaseMsg('');
      setLoading(false);
    }
  };

  const handleResponse = (data: any) => {
    if (data.code === 200) {
      message.success(data.message || '数据已成功从飞书回流到数据库');
      onSuccess?.();
      onClose();
    } else if (data.code === 201) {
      message.info(data.message || '数据内容完全一致，无需回流');
      onClose();
    } else if (data.code === 202 && data.data?.action === 'confirmation_required') {
      const details: ChangeDetail[] = data.data.change_details || [];
      setChangeDetails(details);
      setOperationStats(data.data.operation_stats || null);
      setSelectedIds(new Set<string>(details.map((d) => d.req_id)));
      setSelectAll(true);
      setExpandedRowKeys([]); // 默认收起
      setStep('confirm');
    } else if (data.code === 400 && data.data?.action === 'project_name_mismatch') {
      const { project_name, feishu_doc_name } = data.data;
      message.error(
        `项目名称不匹配：当前项目"${project_name}"与飞书文档"${feishu_doc_name}"不匹配，请检查飞书文档与项目是否对应。`,
        8
      );
    } else if (data.code === 400 && data.data?.action === 'duplicate_req_ids_found') {
      const ids: string[] = data.data.duplicate_req_ids || [];
      message.error(`发现重复的需求编号：${ids.join('、')}。请修正后重试。`, 10);
    } else if (data.code === 400 && data.data?.action === 'incomplete_requirements_found') {
      const count: number = data.data.incomplete_count || 0;
      const details: string[] = data.data.error_summary?.error_details || [];
      const preview = details.slice(0, 10).join(' | ');
      message.error(
        `发现 ${count} 个需求信息不完整。${preview ? '问题详情：' + preview : ''}请补充完整所有必填字段。`,
        12
      );
    } else {
      message.error(data.message || '操作失败');
    }
  };

  const handleConfirm = async () => {
    if (selectedIds.size === 0) {
      message.warning('请至少选择一个需求进行回流');
      return;
    }
    setPhaseMsg('需求回流中...');
    setLoading(true);
    try {
      const data = await performSync({
        user_confirmed: true,
        selected_req_ids: Array.from(selectedIds),
      });
      if (data.code === 200) {
        message.success(data.message || '数据回流成功');
        onSuccess?.();
        onClose();
      } else {
        message.error(data.message || '数据回流失败');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || '数据回流失败，请重试');
    } finally {
      setPhaseMsg('');
      setLoading(false);
      setStep('form');
    }
  };

  const handleCancelSync = async () => {
    setLoading(true);
    try {
      await performSync({ user_cancelled: true });
    } catch {
      // ignore
    } finally {
      setLoading(false);
      setStep('form');
      onClose();
    }
  };

  const toggleOne = (reqId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(reqId) : next.delete(reqId);
      setSelectAll(next.size === changeDetails.length);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectAll(checked);
    setSelectedIds(checked ? new Set(changeDetails.map((d) => d.req_id)) : new Set());
  };

  const isConfirm = step === 'confirm';

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectAll}
          indeterminate={selectedIds.size > 0 && selectedIds.size < changeDetails.length}
          onChange={(e) => toggleAll(e.target.checked)}
        />
      ),
      width: 48,
      render: (_: unknown, record: ChangeDetail) => (
        <Checkbox
          checked={selectedIds.has(record.req_id)}
          onChange={(e) => toggleOne(record.req_id, e.target.checked)}
        />
      ),
    },
    {
      title: '操作',
      dataIndex: 'operation_type',
      width: 110,
      render: (v: string) => <Tag color={OP_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '需求ID',
      dataIndex: 'req_id',
      width: 160,
    },
    {
      title: '变更说明',
      dataIndex: 'operation_description',
      ellipsis: true,
    },
    {
      title: '字段变更数',
      dataIndex: 'field_changes',
      width: 90,
      render: (v: FieldChange[]) => (
        <Tag color={v?.length > 0 ? 'blue' : 'default'}>{v?.length || 0} 项</Tag>
      ),
    },
  ];

  return (
    <Modal
      title={isConfirm ? '确认数据回流' : '从飞书回流需求'}
      open={open}
      onCancel={() => { if (!loading) { setStep('form'); onClose(); } }}
      width={isConfirm ? 1000 : 520}
      style={isConfirm ? { top: 20 } : undefined}
      styles={isConfirm ? {
        body: {
          height: 'calc(100vh - 110px)',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }
      } : undefined}
      mask={{ closable: !loading }}
      footer={null}
      destroyOnHidden={false}
    >
      <Spin spinning={loading} tip={phaseMsg || undefined}>
        {step === 'form' ? (
          <>
            <Alert
              type="warning"
              showIcon
              title="请输入当前项目的飞书多维表格链接，确保项目一致！"
              style={{ marginBottom: 16 }}
            />
            <Form layout="vertical">
              <Form.Item label="飞书多维表格链接" required>
                <Input
                  value={feishuLink}
                  onChange={(e) => setFeishuLink(e.target.value)}
                  placeholder="请输入飞书多维表格链接"
                  disabled={loading}
                  allowClear
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  支持格式：https://momenta.feishu.cn/base/xxxxxxx 或 https://momenta.feishu.cn/wiki/xxxxxxx
                </Text>
              </Form.Item>
            </Form>
            <Alert
              type="info"
              showIcon
              title="数据回流说明"
              description="系统将对比飞书表格与数据库中的数据，并将差异信息展示给您确认后再执行回流操作。"
              style={{ marginBottom: 16 }}
            />
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={onClose} disabled={loading}>取消</Button>
                <Button
                  type="primary"
                  onClick={handleSync}
                  loading={loading}
                  disabled={!feishuLink.trim()}
                >
                  开始数据回流
                </Button>
              </Space>
            </div>
          </>
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              title="发现数据差异，请选择需要回流的需求后确认执行"
              style={{ marginBottom: 16 }}
            />
            {operationStats && (
              <Space style={{ marginBottom: 12 }} wrap>
                {operationStats.add_count > 0 && <Tag color="green">新增: {operationStats.add_count}</Tag>}
                {operationStats.update_count > 0 && <Tag color="blue">修改: {operationStats.update_count}</Tag>}
                {operationStats.delete_count > 0 && <Tag color="red">删除: {operationStats.delete_count}</Tag>}
                {(operationStats.reverse_sync_delete_count ?? 0) > 0 && (
                  <Tag color="orange">反向同步删除: {operationStats.reverse_sync_delete_count}</Tag>
                )}
                <Tag>总计: {operationStats.total_changes}</Tag>
              </Space>
            )}
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              已选择 {selectedIds.size} / {changeDetails.length} 条需求
            </Text>
            <Table
              dataSource={changeDetails}
              columns={columns}
              rowKey="req_id"
              size="small"
              pagination={false}
              style={{ flex: 1, marginBottom: 16 }}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                expandedRowRender: (record) => <ExpandedRow detail={record} />,
                rowExpandable: (record) =>
                  (record.field_changes?.length > 0) ||
                  !!record.new_requirement_info ||
                  !!record.existing_requirement_info,
              }}
            />
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancelSync} disabled={loading}>取消回流</Button>
                <Button
                  type="primary"
                  onClick={handleConfirm}
                  loading={loading}
                  disabled={selectedIds.size === 0}
                >
                  确认回流 ({selectedIds.size})
                </Button>
              </Space>
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default FeishuSyncModal;
